import axios from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { useUserStore } from '@/store/user'

const request = axios.create({
  timeout: 300000
})

const TRANSIENT_SERVER_STATUSES = new Set([502, 503, 504])
const MAX_TRANSIENT_GET_RETRIES = 2

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

async function retryTransientGet(error) {
  const config = error?.config
  const status = error?.response?.status
  const method = String(config?.method || 'get').toLowerCase()
  const retryCount = Number(config?.__transientRetryCount || 0)

  if (
    !config ||
    method !== 'get' ||
    !TRANSIENT_SERVER_STATUSES.has(status) ||
    retryCount >= MAX_TRANSIENT_GET_RETRIES
  ) {
    return undefined
  }

  config.__transientRetryCount = retryCount + 1
  // Short exponential backoff absorbs a brief WAN/NAT reset without making a
  // persistent outage feel unresponsive.  Mutating requests are never retried.
  await delay(250 * (2 ** retryCount))
  return request(config)
}

request.interceptors.request.use(config => {
  const token = localStorage.getItem('token')?.trim()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // 【2026-10 部门/个人智能体授权】把当前用户的身份维度拼到 ai_service 请求上。
  // 后端 list_agents / create_conversation / chat_with_agent 等用它们做授权过滤。
  // 仅向 ai_service 注入；user_service / marketing_service 等不消费这些参数。
  const url = String(config.url || '')
  if (url.includes('/api/v1/ai_service')) {
    try {
      const userStore = useUserStore()
      const scope = userStore.userScope || {}
      const viewerUserId = scope.userId ?? scope.user_id
      const viewerDepartmentId = scope.departmentId ?? scope.department_id
      const viewerIsSuperuser = scope.isSuperuser || scope.is_superuser
      const viewerIsEnterpriseAdmin = scope.isEnterpriseAdmin || scope.is_enterprise_admin

      const method = String(config.method || 'get').toLowerCase()
      // GET：走 query params（最简单、不影响分页签名）
      if (method === 'get') {
        config.params = {
          ...(config.params || {}),
          viewer_user_id: viewerUserId ?? undefined,
          viewer_department_id: viewerDepartmentId ?? undefined,
          viewer_is_superuser: viewerIsSuperuser ? true : undefined,
          viewer_is_enterprise_admin: viewerIsEnterpriseAdmin ? true : undefined
        }
        // axios v1 会把 undefined 当作“不添加”；但为避免发送 literal undefined 字面量，
        // 使用 delete 进一步清理。
        Object.keys(config.params).forEach((k) => {
          if (config.params[k] === undefined) delete config.params[k]
        })
      } else if (
        ['post', 'put', 'patch', 'delete'].includes(method) &&
        config.data !== undefined &&
        config.data !== null
      ) {
        // POST/PUT/PATCH/DELETE：往 body 注入 viewer_department_id。
        // 这里仅写一个部门 ID（其它维度 auth 后端可从 token 取出，
        // 不需要重复送达）。但 list_conversations / create_conversation
        // 等路由同时接受 query 形式，这里同时在 query 里补一份更安全。
        try {
          // 若 data 是 FormData/ArrayBuffer/Stream，原样透传
          if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            // 不动 FormData，避免破坏 multipart boundary
          } else if (typeof config.data === 'string') {
            const parsed = JSON.parse(config.data)
            parsed.viewer_department_id = viewerDepartmentId ?? undefined
            delete parsed.viewer_department_id
            if (parsed.viewer_department_id === undefined) delete parsed.viewer_department_id
            config.data = JSON.stringify(parsed)
          } else if (typeof config.data === 'object') {
            config.data = { ...config.data }
            if (viewerDepartmentId !== undefined && viewerDepartmentId !== null) {
              config.data.viewer_department_id = viewerDepartmentId
            }
          }
        } catch (jsonErr) {
          // 解析失败则不动 body，靠后端用 token 解码
        }
        config.params = {
          ...(config.params || {}),
          viewer_user_id: viewerUserId ?? undefined,
          viewer_department_id: viewerDepartmentId ?? undefined,
          viewer_is_superuser: viewerIsSuperuser ? true : undefined,
          viewer_is_enterprise_admin: viewerIsEnterpriseAdmin ? true : undefined
        }
        Object.keys(config.params).forEach((k) => {
          if (config.params[k] === undefined) delete config.params[k]
        })
      }
    } catch (scopeErr) {
      // 取身份失败就当未登录，后端走 fail-open 分支
    }
  }
  return config
})

function getResponseDetail(error) {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return detail?.message || error?.response?.data?.message || ''
}

function isAuthenticationFailure(error) {
  // 只有真实 HTTP 401 才能清理本地登录态。
  // 502/504 即使携带“未登录”字样，也可能是旧网关或第三方错误包装，
  // 绝不能因支付失败把刚登录的用户强制退出。
  return error?.response?.status === 401
}

request.interceptors.response.use(
  response => response.data,
  async error => {
    // 切页、切筛选条件时列表请求会主动 abort 旧请求；取消不是网络错误，
    // 不能弹出“网络错误”干扰用户，也不能触发登录/权限兜底逻辑。
    if (
      axios.isCancel(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError'
    ) {
      return Promise.reject(error)
    }
    if (error.config?.silent) {
      return Promise.reject(error)
    }

    const retriedResponse = await retryTransientGet(error)
    if (retriedResponse !== undefined) {
      return retriedResponse
    }

    if (error.response) {
      const status = error.response.status
      // 只有明确的 401 鉴权响应才允许触发 logout；支付 502/504 保留当前会话。
      if (isAuthenticationFailure(error)) {
        if (error.config?.url?.includes('/api/v1/user_service/auth/login')) {
          return Promise.reject(error)
        }
        const userStore = useUserStore()
        userStore.logout()
        ElMessage.error('登录已过期，请重新登录')
        error.__requestToastShown = true
        router.replace('/login')
      } else if (status === 403) {
        ElMessage.error('没有权限')
        error.__requestToastShown = true
      } else if (status === 404) {
        ElMessage.error('资源不存在')
        error.__requestToastShown = true
      } else {
        const message = getResponseDetail(error) || '请求失败'
        ElMessage.error(message)
        error.__requestToastShown = true
      }
    } else {
      ElMessage.error('网络错误')
      error.__requestToastShown = true
    }
    return Promise.reject(error)
  }
)

export default request
